import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleHome } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Mandatory password-change dialog shown when the current user has
 * `force_password_change=true` on their /me record. Not dismissible until
 * a new password is set.
 */
export const ForcePasswordChangeDialog = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  const open = !!user?.force_password_change;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      // Capture role BEFORE refresh so we route correctly even if refresh flickers
      const role = user?.role;
      await api.post("/auth/change-password", { new_password: pw });
      toast.success("Password updated. Welcome to Belomotors.");
      await refresh?.();
      // Route the user into their role-appropriate portal so they don't
      // land on the public marketplace after clearing the force flag.
      navigate(roleHome(role), { replace: true });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* non-dismissible */ }}>
      <DialogContent className="sm:max-w-md" data-testid="force-password-change-dialog" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-brand-light text-brand flex items-center justify-center mx-auto">
            <KeyRound size={22} />
          </div>
          <DialogTitle className="text-center font-display text-2xl">Set your new password</DialogTitle>
          <DialogDescription className="text-center">
            Your account was provisioned by an admin with a temporary password. Please choose a new password before continuing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">New password</Label>
            <Input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} autoFocus data-testid="force-pw-new-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Confirm new password</Label>
            <Input type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="force-pw-confirm-input" />
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-brand hover:bg-brand-hover text-white" data-testid="force-pw-submit-btn">
            {saving ? "Saving…" : "Set new password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ForcePasswordChangeDialog;
